import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { nanoid } from 'nanoid';
import { preloadVideos } from '@components/AssetPreloader';
import useAsyncEffect from 'use-async-effect';

import { useMytaverse } from '@providers/MytaverseProvider';
import { LeaveEventReason } from '@providers/MytaverseProvider/interfaces';

import EventsService from '@services/EventsService';
import BrandingsService, {
  type IBrandingApi,
} from '@services/BrandingsService';
import DynamicMapTriggersService from '@services/DynamicMapTriggersService';
import { IDynamicMapTrigger } from '@shared/interfaces/dynamicMapTrigger';
import {
  DegradedService,
  IEventParticipant,
} from '@interfaces/eventParticipant';
import { getDeviceProfile, mapFollowers } from './helpers';
import { resolveMapEditorRoomScope } from './mapEditorRoomScope';
import type {
  MapEditorModeState,
  MapLayoutRefusal,
  MapLayoutStandingRoom,
} from '@core/dashboard/components/EditorMode/mapEditorUnreal';
import { useSpotLanguageSync } from './hooks/useSpotLanguageSync';
import { useVisitId } from './hooks/useVisitId';
import { IAvatarSkin } from '@interfaces/avatarSkin';
import type { ILiveBrandingEntity } from '@interfaces/map';
import {
  EventStatus,
  ExperienceFeatures,
  IEvent,
  IPixelStreamingClient,
} from '@interfaces/event';
import {
  IParticipant,
  IParticipantRegion,
  ParticipantState,
} from '@interfaces/participants';
import { IRoom } from '@interfaces/rooms';
import {
  IRealtimeEntity,
  isRealtimeEntityDirty,
  realtimeEntityFromSnapshot,
} from '@interfaces/realtimeEntities';
import SpawnedEntitiesService from '@services/SpawnedEntitiesService';
import ParticipantsService from '@services/ParticipantsService';
import { Outlet, useParams } from 'react-router-dom';
import { IPointOfInterest } from '@interfaces/pointsOfInterest';
import { NOTIFICATION_TYPES } from '@components/Notification';

import {
  useAvatarMessage,
  useEventParticipantData,
  useInitMessage,
  useParticipantsState,
} from './hooks';
import {
  IWebsocketMessageFlag,
  IUnrealScreenSize,
  SendMutedStateMessageEnum,
  StreamingProviders,
  UpdateParticipantType,
} from './interfaces';
import { useMytaStreamsSession } from '../../components/DashboardContent/MytaStreams/MytaStreamsProvider';
import AnalyticsService from '@services/AnalyticsService';
import { SendToBriefcasePropsTypeFull } from '../../components/DashboardContent/interfaces';
import type { EditorFocus } from '@core/dashboard/components/EditorMode/editorCategories';
import { useChatState } from '@core/chat/context';
import { useInitGameCastStream } from './hooks/streamProviders';
import { usePixelStreamingProvider } from './hooks/pixelStreamingProvider';
import { IFollowerData } from '@interfaces/followers';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';
import { MytaverseLogger } from '@helpers/logger';
import { reportStreamInitializationEvent } from '@helpers/sentry';
import { useCoreweave } from '../../components/DashboardContent/CoreWeave/CoreWeaveProvider';
import { PixelStreaming } from '@tensorworks/libspsfrontend';
import { PixelStreaming as MytaPixelStreamingClient } from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5';
import { useGamecast } from '../../components/DashboardContent/GameCast/GameCastProvider';
import { useBackButton } from './hooks/eventListeners';
import {
  BillingCloseReason,
  useBillingInvoice,
} from './hooks/useBillingInvoice';
import { MytaCutReason } from '@core/dashboard/components/DashboardContent/MytaStreams/helpers';
import { IGamecastVideoStats } from '../../components/DashboardContent/GameCast/interfaces';
import EventFullLoadingPage from '@core/event/pages/EventFullLoadingPage';
import { env } from 'env';

import { AnalyticsType } from '@interfaces/analytics';
import { useEventContext } from '@providers/EventProvider';
import {
  IStartStreamSessionOutput,
  StreamSessionStatus,
} from '@interfaces/gamecast';
import { default as mytaverseEventLogger } from './logger';
import {
  getClientSpecificCanShareScreenOnScreen,
  getClientSpecificCanShareWebCameraOnScreen,
  getClientSpecificCanShowWebCameraOnAvatar,
  getClientSpecificCanSpeak,
  getClientSpecificUserEventPermissions,
  getClientSpecificUserEventRole,
} from '@helpers/clientSpecificUtils';
import { isDesktop } from 'react-device-detect';
import ParticipantScopes from '@constants/scopes';
import { NavigationModeType } from '@constants/navigationMode';
import {
  formatBuildVersionLabel,
  isEventFeatureEnabled,
  isSinglePlayerEvent,
} from '@helpers/events';
import { stopAssistantAudio } from '@core/assistant/providers/globals';
import { useAssistantTeardownOnDisable } from '@core/assistant/useAssistantTeardownOnDisable';

// How long a reload rebind keeps putting the participant back in the room its slot is already in. Long
// enough to outlive the reconnect handshake that blanks it (the server's JOINED_TO_EVENT, plus the 30 s
// connection re-sync that answers with the not-yet-updated record), short enough that it is over before a
// user could teleport out of that room by hand.
const SEATED_ROOM_RESTORE_WINDOW_MS = 45000;
// How long a playing stream may go without anyone announcing a room before the hub seats the user in the room
// it asked UE for. A healthy entry resolves in a few seconds; a UE that ignored the INIT never resolves at all.
const SEATED_ROOM_FALLBACK_MS = 20000;

// Where the watchdog's verdict turns into money: only a cut reason takes a
// segment out of billing, so the mapping is stated here rather than inside the
// provider that detects the failure. Both values are cuts on purpose — the seat
// is gone either way, and a recovered chain is billed in full regardless.
const MYTA_CUT_BILLING_REASONS: Record<MytaCutReason, BillingCloseReason> = {
  [MytaCutReason.Stalled]: BillingCloseReason.Crash,
  [MytaCutReason.NeverFramed]: BillingCloseReason.StreamTimeout,
  // A seat the server took away (or this page left for a reclaimed box) ends the stream session like a
  // crash does; the chain rule (metering §18) bills it when the visitor comes back on the next seat.
  [MytaCutReason.SeatLost]: BillingCloseReason.Crash,
};

export const MytaverseEventProvider = () => {
  const { eventId: currentEventId } = useParams();

  const {
    token,
    userId,
    user,
    userPermissions,
    sendJSONMessageToWebSocket,
    isWebsocketOpen,
    sendMessageToEvent,
    websocketSessionId,
    isLogoutClicked,
    sendMessageToUnreal,
    navigateToEventsPage,
    getLeaveEventReason,
    resetLeaveEventReason,
    setUserPermissions,
    selectedLanguage,
  } = useMytaverse();

  const [reaction, setReaction] = useState<string | null>(null);
  const [emoji, setEmoji] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [openAdminSettingsModal, setOpenAdminSettingsModal] = useState(false);
  // Drone camera is an admin-tools toggle. It lives here (not in the AdminTools
  // component) so it survives that component unmounting when hub-admin-tools
  // access is lost, and so the permission-loss effect below can disable it (MV-1303).
  const [isDroneMode, setIsDroneMode] = useState(false);
  const [isHandRaised, setIsHandRaised] = useState(false);
  const [muted, setMuted] = useState(true);
  const [currentEvent, setCurrentEvent] = useState<IEvent>();
  const [eventLoaded, setEventLoaded] = useState<boolean>(false);
  const [rooms, setRooms] = useState<IRoom[]>([]);
  const [currentRoom, setCurrentRoom] = useState<IRoom | null>(null);
  const [developersTerminalMessages, setDevelopersTerminalMessages] = useState<
    string[]
  >([]);
  const [openLeaveRegionDialog, setOpenLeaveRegionDialog] = useState(false);
  const [gameReadyToPlay, setGameReadyToPlay] = useState(true);
  const [openCameraPublisherDialog, setOpenCameraPublisherDialog] =
    useState(false);
  const [ue5WebsocketConnected, setUe5WebsocketConnected] = useState(false);
  const [ue5CoreWeaveDisabled, setUe5CoreWeaveDisabled] = useState(false);
  const [currentRegion, setCurrentRegion] = useState<IParticipantRegion | null>(
    null,
  );
  const [
    currentRoomDolbySpatialAudioScale,
    setCurrentRoomDolbySpatialAudioScale,
  ] = useState<number>(80);
  const [isTeleporting, setIsTeleporting] = useState(false);
  const [teleportFinished, setTeleportFinished] = useState(false);
  const [poiPreviewSrc, setPoiPreviewSrc] =
    useState<SendToBriefcasePropsTypeFull | null>(null);
  const [initMessageSended, setInitMessageSended] = useState(false);
  const [previousSkin, setPreviousSkin] = useState<IAvatarSkin | null>(null);
  const [currentEventParticipant, setCurrentEventParticipant] =
    useState<IEventParticipant | null>(null);
  const [gameCastStreamRequestSended, setGameCastStreamRequestSended] =
    useState(false);
  const [previousCustomAvatar, setPreviousCustomAvatar] = useState<
    string | null
  >(null);
  const [pointsOfInterest, setPointsOfInterest] = useState<
    IPointOfInterest[] | null
  >(null);
  const [userFiles, setUserFiles] = useState<IPointOfInterest[]>([]);
  const [isGamecastCrash, setIsGamecastCrash] = useState(false);
  const [isGamecastTimeout, setIsGamecastTimeout] = useState(false);
  const [currentParticipant, setCurrentParticipant] = useState<
    IParticipant | undefined
  >();
  const [teleportingToRoom, setTeleportingToRoom] = useState<IRoom | null>(
    null,
  );
  const [teleportEnabled, setTeleportEnabled] = useState(false);
  const [isTeleportingToRoomByUnreal, setIsTeleportingToRoomByUnreal] =
    useState(false);
  const [participants, setParticipants] = useState<IParticipant[]>([]);
  const [loadingNewParticipants, setLoadingNewParticipants] = useState(false);
  const [showAvaturnModal, setShowAvaturnModal] = useState(false);
  // const [recreateAvaturnAvatar, setRecreateAvaturnAvatar] = useState(null);
  const [openTeleportToRoomDialog, setOpenTeleportToRoomDialog] =
    useState(false);
  const [selectedTeleportRoom, setSelectedTeleportRoom] =
    useState<IRoom | null>(null);
  const [dolbyToken, setDolbyToken] = useState<string>('');
  const [streamChatToken, setStreamChatToken] = useState<string>('');
  const [streamChatMemberId, setStreamChatMemberId] = useState<string>('');
  const [unavailableServices, setUnavailableServices] = useState<
    DegradedService[]
  >([]);
  const [enableSoulmachinesAssistant, setEnableSoulmachinesAssistant] =
    useState(false);
  const [assistantScriptUpdateTick, setAssistantScriptUpdateTick] = useState(0);

  const [showCustomSpotsEditor, setShowCustomSpotsEditor] =
    useState<boolean>(false);
  const [showCustomizeSpotModal, setShowCustomizeSpotModal] =
    useState<boolean>(false);
  const [customisationItemSelectedTick, setCustomisationItemSelectedTick] =
    useState(0);
  const [selectedCustomizationTag, setSelectedCustomizationTag] = useState<
    string | null
  >(null);
  const [roomSpots, setRoomSpots] = useState<any[]>([]);
  const [selectedCustomizationUeId, setSelectedCustomizationUeId] = useState<
    string | null
  >(null);
  const [selectedCustomizationTargetSize, setSelectedCustomizationTargetSize] =
    useState<IUnrealScreenSize | null>(null);
  const [selectedCustomizationSpotType, setSelectedCustomizationSpotType] =
    useState<'IMAGE' | 'VIDEO' | 'VIDEO_STATIC' | 'QR' | null>(null);
  const [customizationSelectionRevision, setCustomizationSelectionRevision] =
    useState(0);
  const [isBrandingModeEnabled, setIsBrandingModeEnabled] =
    useState<boolean>(false);
  const [brandingActionRequest, setBrandingActionRequest] = useState<
    'library' | 'create' | null
  >(null);
  const [brandingEditorFocus, setBrandingEditorFocus] =
    useState<EditorFocus>('environment');
  const [isEditorInspectorOpen, setIsEditorInspectorOpen] =
    useState<boolean>(false);
  const [assistantOpenRequestNonce, setAssistantOpenRequestNonce] =
    useState<number>(0);
  const requestAssistantOpen = useCallback(() => {
    setAssistantOpenRequestNonce((prev) => prev + 1);
  }, []);
  const [pendingAssistantRegionTrigger, setPendingAssistantRegionTrigger] =
    useState<{
      trigger: string;
      nonce: number;
      hasContent: boolean;
    } | null>(null);
  const requestAssistantRegionChange = useCallback(
    (trigger: string, hasContent: boolean) => {
      setPendingAssistantRegionTrigger((prev) => {
        const nonce = (prev?.nonce ?? 0) + 1;
        return { trigger, nonce, hasContent };
      });
      mytaverseEventLogger.log(
        `[assistant] requestAssistantRegionChange requested`,
        { trigger, hasContent },
      );
    },
    [],
  );
  const suppressedCustomizationSelectIdRef = useRef<string | null>(null);
  const suppressNextCustomizationSelect = useCallback((ueId: string) => {
    suppressedCustomizationSelectIdRef.current = ueId;
  }, []);
  const [currentBrandingId, setCurrentBrandingId] = useState<string | null>(
    null,
  );
  const [brandingSpots, setBrandingSpots] = useState<any[]>([]);
  const [brandingHotspots, setBrandingHotspots] = useState<any[]>([]);
  const [currentBranding, setCurrentBranding] = useState<IBrandingApi | null>(
    null,
  );
  const [pendingEditorHotspotSelection, setPendingEditorHotspotSelection] =
    useState<{ hotspotId: string; nonce: number } | null>(null);
  const requestEditorHotspotSelection = useCallback((hotspotId: string) => {
    setPendingEditorHotspotSelection((prev) => {
      const nonce = (prev?.nonce ?? 0) + 1;
      return { hotspotId, nonce };
    });
  }, []);
  // What Unreal reports standing, room by room: the element, the key that placement is standing
  // under, and — once its level has been read — the addressable id of every spot inside it.
  const [currentGeometryRooms, setCurrentGeometryRooms] = useState<
    MapLayoutStandingRoom[] | null
  >(null);
  // The other half of the same report. Kept beside the room ids rather than folded into them because
  // the two answer different questions — what is standing, and which requested rooms were turned
  // down — and only the refusals can explain a layout that came back shorter than it was published.
  const [currentGeometryRefusals, setCurrentGeometryRefusals] = useState<
    MapLayoutRefusal[] | null
  >(null);
  // The lobby of the same report, taken out of the room list on the way in and kept here instead —
  // see parseGeometryStartRoom. Null both before a report and for a world that opens on the map's
  // baked entrance; the rooms above are what say which of the two it is.
  const [currentGeometryStartRoom, setCurrentGeometryStartRoom] =
    useState<MapLayoutStandingRoom | null>(null);
  // The same, for the draft this author is assembling. Held apart from the two above rather than
  // reusing them: those describe the published world and every participant is looking at it, while
  // this describes rows only this browser has asked for. Re-sent on every edit, so it always
  // describes the draft currently on screen.
  const [previewRefusals, setPreviewRefusals] = useState<
    MapLayoutRefusal[] | null
  >(null);
  // Brandable objects Unreal reports standing right now (MV-1905). Every report is the whole world,
  // so this is replaced rather than merged, and it lives here rather than in the editor's provider
  // because neither side of it is inside that provider's subtree: the websocket handler that writes
  // it runs in `DashboardContent`'s hooks, and `AdminTools` in the top bar reads it to decide
  // whether the Objects focus may be offered at all.
  const [liveBrandingEntities, setLiveBrandingEntities] = useState<
    ILiveBrandingEntity[] | null
  >(null);
  const [editorModeState, setEditorModeState] =
    useState<MapEditorModeState | null>(null);
  const [pendingHotspotPlacement, setPendingHotspotPlacement] = useState<{
    brandingHotspotId: string;
    isCreated: boolean;
  } | null>(null);
  const [hotspotLiveTransform, setHotspotLiveTransform] = useState<{
    brandingHotspotId: string;
    position?: number[];
    rotation?: number[];
    scale?: number | number[];
    nonce: number;
  } | null>(null);
  const publishHotspotLiveTransform = useCallback(
    (update: {
      brandingHotspotId: string;
      position?: number[];
      rotation?: number[];
      scale?: number | number[];
    }) => {
      setHotspotLiveTransform((prev) => ({
        ...update,
        nonce: (prev?.nonce ?? 0) + 1,
      }));
    },
    [],
  );

  const [realtimeEntities, setRealtimeEntities] = useState<IRealtimeEntity[]>(
    [],
  );
  const [realtimeEntitiesRevision, setRealtimeEntitiesRevision] = useState(0);
  const realtimeEntitiesRoomIdRef = useRef<string | null>(null);
  const realtimeEntitiesLoadSequenceRef = useRef(0);
  const [realtimeEntityLiveTransform, setRealtimeEntityLiveTransform] =
    useState<{
      entityId: string;
      position?: number[];
      rotation?: number[];
      scale?: number[];
      nonce: number;
    } | null>(null);

  /**
   * Merges a partial update into the tracked entity, or adds it when new.
   *
   * A partial naming an unknown id is dropped rather than added: it is a stale
   * report (a gizmo drag settling after the entity was deleted), and a row
   * without a type could never be labelled or edited.
   */
  const upsertRealtimeEntity = useCallback(
    (update: Partial<IRealtimeEntity> & { entityId: string }) => {
      setRealtimeEntities((prev) => {
        const existing = prev.find(
          (entity) => entity.entityId === update.entityId,
        );

        if (!existing) {
          if (!update.typeId || !update.displayName || !update.name) {
            return prev;
          }
          return [...prev, update as IRealtimeEntity];
        }

        return prev.map((entity) =>
          entity.entityId === update.entityId
            ? {
                ...entity,
                ...update,
                properties: {
                  ...entity.properties,
                  ...update.properties,
                },
              }
            : entity,
        );
      });
    },
    [],
  );

  /**
   * Forgets one entity after its DELETE was sent — its id is free for reuse.
   *
   * @param entityId - Entity to drop from the session registry.
   */
  const removeRealtimeEntity = useCallback((entityId: string) => {
    setRealtimeEntities((prev) =>
      prev.filter((entity) => entity.entityId !== entityId),
    );
  }, []);

  /**
   * Republishes the engine's settled gizmo-drag report for the open inspector,
   * bumping a nonce so consecutive identical transforms still notify — the
   * same discipline as publishHotspotLiveTransform above.
   *
   * @param update - The reported entity id plus whichever transform fields the report carried.
   */
  const publishRealtimeEntityLiveTransform = useCallback(
    (update: {
      entityId: string;
      position?: number[];
      rotation?: number[];
      scale?: number[];
    }) => {
      setRealtimeEntityLiveTransform((prev) => ({
        ...update,
        nonce: (prev?.nonce ?? 0) + 1,
      }));
    },
    [],
  );

  /**
   * Drops every tracked entity and the retained drag report, and bumps the
   * revision so an open editor knows to load the room's saved set again.
   *
   * Called for the moments the engine's live tracking map is known to be empty
   * — a level change, a fresh engine session — and when another admin's save
   * or delete lands.
   */
  const clearRealtimeEntities = useCallback(() => {
    setRealtimeEntities([]);
    setRealtimeEntityLiveTransform(null);
    setRealtimeEntitiesRevision((revision) => revision + 1);
  }, []);

  /**
   * Marks the room's saved set as changed elsewhere, without touching the rows
   * this client holds — another admin's save or delete must not discard the
   * unsaved work in this one's panel.
   */
  const bumpRealtimeEntitiesRevision = useCallback(() => {
    setRealtimeEntitiesRevision((revision) => revision + 1);
  }, []);

  /**
   * Loads a branding's saved entities into the registry.
   *
   * Called from the editor layer only: the read is admin-scoped, so firing it
   * for every participant on every room change earns a 403 apiece.
   *
   * A load that resolves after the room moved on, or after a newer load
   * started, is discarded — otherwise the previous branding's entities land in
   * the panel, and the next Save writes them into the wrong partition.
   *
   * @param brandingId - Branding the editor has open.
   * @returns Nothing; the registry is updated in place.
   */
  const reloadRealtimeEntities = useCallback(
    async (brandingId: string | null) => {
      const roomId = currentRoom?.roomId;
      const sequence = realtimeEntitiesLoadSequenceRef.current + 1;
      realtimeEntitiesLoadSequenceRef.current = sequence;

      setRealtimeEntityLiveTransform(null);

      if (!roomId || !brandingId) {
        setRealtimeEntities([]);
        return;
      }

      const isStale = () =>
        realtimeEntitiesLoadSequenceRef.current !== sequence ||
        realtimeEntitiesRoomIdRef.current !== roomId;

      try {
        const saved = await SpawnedEntitiesService.getBrandingSpawnedEntities(
          roomId,
          brandingId,
        );

        if (isStale()) {
          return;
        }

        // Merged, not assigned: a reload can be triggered by another admin's
        // save, and this admin's spawned-but-unsaved rows and pending edits
        // are the one thing the backend cannot tell us about.
        setRealtimeEntities((prev) => {
          const pending = new Map(
            saved
              .map(realtimeEntityFromSnapshot)
              .map((entity) => [entity.entityId, entity]),
          );

          const merged = prev.reduce<IRealtimeEntity[]>((kept, entity) => {
            const fresh = pending.get(entity.entityId);

            if (!fresh) {
              // Gone from this branding: dropped by another admin if it was
              // ever saved, still this client's to edit if it was not.
              return entity.saved ? kept : [...kept, entity];
            }

            pending.delete(entity.entityId);

            return [...kept, isRealtimeEntityDirty(entity) ? entity : fresh];
          }, []);

          return [...merged, ...pending.values()];
        });
      } catch (loadError) {
        console.error(
          `Failed to load saved spawned entities: ${
            (loadError as Error)?.message
          }`,
          { roomId, brandingId },
        );

        if (!isStale()) {
          setRealtimeEntities([]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentRoom?.roomId],
  );

  // The engine tracks spawned entities only within the running level, so a room
  // change orphans every id the hub still lists — reset rather than offer edits
  // the engine would refuse. Null is ignored, not treated as a change:
  // `currentRoom` transiently blanks during participant-state sync passes (the
  // same flicker resolveMapEditorRoomScope exists to ride out), and wiping on it
  // would orphan entities the engine still tracks.
  useEffect(() => {
    const roomId = currentRoom?.roomId ?? null;
    if (!roomId || realtimeEntitiesRoomIdRef.current === roomId) {
      return;
    }
    realtimeEntitiesRoomIdRef.current = roomId;
    clearRealtimeEntities();
  }, [currentRoom?.roomId, clearRealtimeEntities]);

  useSpotLanguageSync({
    language: selectedLanguage,
    isEventActive: !!currentEvent,
    brandingSpots,
    roomSpots,
    setBrandingSpots,
    setRoomSpots,
    sendMessageToUnreal,
  });

  const [showExternalKeyboardOverlay, setShowExternalKeyboardOverlay] =
    React.useState(false);
  const [externalKeyboardConnected, setExternalKeyboardConnected] =
    React.useState(!isDesktop);

  const [isUserEnteringEvent, setIsUserEnteringEvent] = useState(false);
  const fallbackScreenshotDownloadUrlRef = useRef(null);
  const [showExitButton, setShowExitButton] = useState<IWebsocketMessageFlag>({
    flag: false,
    timestamp: 0,
  });
  const [isHideWebUI, setIsHideWebUI] = useState<IWebsocketMessageFlag>({
    flag: false,
    timestamp: 0,
  });
  const [isHideWebUIExceptAssistant, setIsHideWebUIExceptAssistant] =
    useState<IWebsocketMessageFlag>({
      flag: false,
      timestamp: 0,
    });
  const [isHideAllExceptTopBar, setIsHideAllExceptTopBar] =
    useState<IWebsocketMessageFlag>({
      flag: false,
      timestamp: 0,
    });
  const [screenshotDownloadUrl, setScreenshotDownloadUrl] = useState<
    string | null
  >(null);

  const [familiarizeState, setFamiliariseState] = useState(null);
  const [aiTriggerName, setAiTriggerName] = useState('');
  const [dynamicAiTriggers, setDynamicAiTriggers] = useState<
    IDynamicMapTrigger[]
  >([]);

  const dynamicAiTriggersMapId = currentRoom?.map?.mapId;

  useEffect(() => {
    if (!dynamicAiTriggersMapId) {
      setDynamicAiTriggers([]);
      return;
    }

    let cancelled = false;

    DynamicMapTriggersService.getDynamicMapTriggers(dynamicAiTriggersMapId)
      .then((triggers) => {
        if (!cancelled) {
          setDynamicAiTriggers(triggers);
        }
      })
      .catch((error) => {
        console.error(
          `Failed to fetch dynamic map triggers: ${(error as Error).message}`,
          { mapId: dynamicAiTriggersMapId },
        );
      });

    return () => {
      cancelled = true;
    };
  }, [dynamicAiTriggersMapId]);
  const [objectPoiId, setObjectPoiId] = useState<string | null>('');
  const [spotFullscreenViewId, setSpotFullscreenViewId] = useState<
    string | null
  >(null);
  const [spotFullscreenViewTrigger, setSpotFullscreenViewTrigger] = useState<
    string | null
  >(null);
  const [spotFullscreenViewStartAt, setSpotFullscreenViewStartAt] =
    useState<number>(0);

  const {
    REACT_APP_EXTERNAL_EVENT_TAG,
    REACT_APP_UE_INITIALIZATION_TIMEOUT_SECONDS = 300,
  } = env;

  useEffect(() => {
    if (!REACT_APP_EXTERNAL_EVENT_TAG) {
      return;
    }
    const hasExternalEventTag =
      currentEvent?.eventTags?.includes(REACT_APP_EXTERNAL_EVENT_TAG) === true;

    if (hasExternalEventTag) {
      setIsHideWebUI((prev) => {
        if (prev.flag) {
          return prev;
        }
        return { flag: true, timestamp: Number.MAX_SAFE_INTEGER };
      });
    }
  }, [currentEvent?.eventTags, REACT_APP_EXTERNAL_EVENT_TAG, setIsHideWebUI]);

  const [showControls, setShowControls] = useState<boolean>(true);
  const [sharingWindowFullsceen, setSharingWindowFullscreen] = useState(false);
  const [isMinimizedScreenSharing, setIsMinimizedScreenSharing] =
    useState(false);
  const [openDevelopersTerminal, setOpenDevelopersTerminal] = useState(false);
  const [pendingFollowersData, setPendingFollowersData] = useState<
    IFollowerData[]
  >([]);
  const [acceptedFollowersData, setAcceptedFollowersData] = useState<
    IFollowerData[]
  >([]);
  const [preserveAcceptedFollowersId, setPreserveAcceptedFollowersId] =
    useState<string[]>([]);
  const [isParticipantRoomChanged, setIsParticipantRoomChanged] =
    useState(false);
  const [events, setEvents] = useState<IEvent[]>([]);
  const [playVideoUrl, setPlayVideoUrl] = useState<string | null>(null);
  const [coreweavePixelStreaming, setCoreweavePixelStreaming] =
    useState<PixelStreaming | null>(null);
  const [mytaPixelStreaming, setMytaPixelStreaming] =
    useState<MytaPixelStreamingClient | null>(null);
  const gamecastStats = useRef<IGamecastVideoStats | null>(null);
  const [showGamecastStats, setShowGamecastStats] = useState(false);
  const [isUEModalActionInProgress, setIsUEModalActionInProgress] =
    useState(false);

  const ueInitializationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const visitIdRef = useVisitId(currentEventId);

  const trackEventAnalytics = React.useCallback(
    (type: AnalyticsType, payload: any = {}) => {
      if (websocketSessionId) {
        AnalyticsService.track(type, new Date().getTime(), {
          eventId: currentEventId,
          participantId: userId,
          sessionId: websocketSessionId,
          payload: {
            // deviceType feeds the backend device/geo stats counters (MV-1225)
            deviceType: getDeviceProfile(),
            visitId: visitIdRef.current,
            ...payload,
          },
        });
      }
    },
    [websocketSessionId],
  );

  const {
    streamingProvider,
    setStreamingProvider,
    pixelStreamingClient,
    isNativeProviderCheckPending,
  } = usePixelStreamingProvider({ currentEventId, trackEventAnalytics });
  const gameliftStreamsSessionKeyRef = useRef<string>(nanoid(20));

  // Read via ref inside generateNewGameliftStreamsSessionKey (empty useCallback deps)
  // to avoid logging a stale pixelStreamingClient.
  const pixelStreamingClientRef = useRef<IPixelStreamingClient | null>(null);
  useEffect(() => {
    pixelStreamingClientRef.current = pixelStreamingClient;
  }, [pixelStreamingClient]);

  const { setParticipantState } = useParticipantsState({
    currentParticipantId: userId as string,
    currentEventId,

    currentParticipant,
    setParticipants,

    participants,
    setCurrentParticipant,

    currentRoom,
    setCurrentRoom,
    currentRegion,
    setCurrentRegion,
    setIsParticipantRoomChanged,
    rooms,
    websocketSessionId,
    trackEventAnalytics,
  });

  // Reload rebind: after an F5 the seat survives server-side, but the participant comes back with no room —
  // handleJoinedToEvent clears roomId on every entry and the JOINED_TO_ROOM that would restore it was sent by
  // UE before this page existed. `currentRoom` is DERIVED, never a place to write: useParticipantsState nulls
  // it on every pass where the participant's own roomId is empty, so writing it directly is undone within a
  // tick — measured live 2026-08-12, "Room changed to Map_SpiralMuseum" was followed by a blank page with no
  // #mytaStreamsWrapper while the slot kept streaming the museum at 30 fps. Restoring the participant's roomId
  // instead lets that same effect resolve the room AND announce JOINED_TO_ROOM, which is what re-associates
  // the surviving seat with the NEW websocket session.
  const seatedRoomRef = useRef<{
    mapName?: string;
    until: number;
    applied: number;
  } | null>(null);
  const [seatedRoomAttempt, setSeatedRoomAttempt] = useState(0);

  const restoreSeatedRoom = useCallback((mapName?: string) => {
    if (seatedRoomRef.current) {
      return;
    }

    seatedRoomRef.current = {
      mapName,
      until: Date.now() + SEATED_ROOM_RESTORE_WINDOW_MS,
      applied: 0,
    };
    setSeatedRoomAttempt((attempt) => attempt + 1);
  }, []);

  // Re-assert for the whole entry window, not once: writing roomId is not enough because the server answers
  // the reconnect with JOINED_TO_EVENT for our own participant, whose handler blanks roomId again — measured
  // 2026-08-12, the wipe landed in the same second as the restore ("[room-wipe] JOINED_TO_EVENT … clearing
  // roomId") and the page went blank behind a stream that was already decoding. The window closes well before
  // a user can teleport, so the legitimate LEFT_ROOM → JOINED_TO_ROOM gap of a real teleport is never fought.
  useEffect(() => {
    const pending = seatedRoomRef.current;

    if (!pending || !userId || !currentParticipant) {
      return;
    }

    if (currentParticipant.roomId) {
      return;
    }

    if (Date.now() > pending.until || pending.applied >= 10) {
      return;
    }

    const tail = (pending.mapName ?? '').split('/').filter(Boolean).pop();
    const room =
      (tail &&
        rooms.find((candidate) =>
          [
            candidate.map?.ueIdentifier,
            candidate.map?.name,
            candidate.name,
          ].includes(tail),
        )) ||
      rooms.find((candidate) => candidate.isDefault) ||
      rooms[0];

    if (!room) {
      return;
    }

    pending.applied += 1;
    mytaverseEventLogger.log(
      `Reload rebind — putting the participant back in ${room.name} (slot map ${
        pending.mapName ?? '-'
      }, attempt ${pending.applied})`,
    );
    setParticipantState(userId as string, { roomId: room.roomId });
    setSeatedRoomAttempt((attempt) => attempt + 1);
  }, [
    seatedRoomAttempt,
    rooms,
    currentParticipant,
    setParticipantState,
    userId,
  ]);

  const {
    participantsSound,
    gameSound,
    assistantSound,
    setGameSoundHandle,
    setParticipantsSoundHandle,
    setAssistantSoundHandle,
  } = useEventParticipantData({
    currentEvent,
    currentEventParticipant,
  });

  const { launchArgs, isEventRun } = useEventContext();
  //const { showNotification, getBadRequestNotification } = useNotification();
  const { setOpen: setOpenChat } = useChatState();
  const { isDebugMode } = useMytaverse();

  const [billingInvoiceId, setBillingInvoiceId] = useState<string | null>(null);
  const [billingStreamSessionId, setBillingStreamSessionId] = useState<
    string | null
  >(null);

  const generateNewGameliftStreamsSessionKey = useCallback(() => {
    if (isDebugMode) {
      return '';
    }

    const gameliftStreamsSessionKey = nanoid(20);

    mytaverseEventLogger.log(
      `New gamelift streams session key: ${gameliftStreamsSessionKey}`,
    );

    const currentPixelStreamingClient = pixelStreamingClientRef.current;
    mytaverseEventLogger.log(
      `Pixel streaming client build (live): ${formatBuildVersionLabel(
        currentPixelStreamingClient?.version,
        currentPixelStreamingClient?.clientChangeset,
      )}`,
    );

    gameliftStreamsSessionKeyRef.current = gameliftStreamsSessionKey;

    return gameliftStreamsSessionKey;
  }, [currentEventId]);

  const { initMessageHandler } = useInitMessage({
    currentEvent,
    currentParticipant,
    currentEventParticipant,
    dolbyToken,
    teleportingToRoom,
    teleportEnabled,
    token,
    websocketSessionId,
    user,
    userPermissions,
    generateNewGameliftStreamsSessionKey,
  });

  const mytaStreams = useMytaStreamsSession({
    enabled: streamingProvider === StreamingProviders.MytaStreams,
    currentEvent,
    pixelStreamingClient,
    initMessageHandler,
    initMessageSended,
    ue5WebsocketConnected,
    setInitMessageSended,
    setGameReadyToPlay,
    gameSound,
    setMytaPixelStreaming,
    setTeleportFinished,
    restoreSeatedRoom,
    trackEventAnalytics,
  });

  const isMytaStreams = streamingProvider === StreamingProviders.MytaStreams;

  // Called below the MYTA_STREAMS session so it can read its signals, and still
  // above the unmount effect that sends LEAVE_EVENT — cleanups run in order, and
  // that one strips the `eventId` the close is checked against.
  const { handleStale: handleBillingStale } = useBillingInvoice({
    // GameCast rides `isEventRun` alone; that never drops on the MYTA_STREAMS path,
    // so there it only opens and three more terms end the segment. A decoded frame on
    // the current peer, because `playStream` fires before playback is even attempted;
    // `isEventRun` again, since the Enter button renders only while the stream has not
    // started and the loader is free by design; and a held lease, because the resumer
    // can re-latch off a slot already handed back. The editor has no GPU to bill.
    isWorldOnScreen: isMytaStreams
      ? mytaStreams.pictureLive &&
        isEventRun &&
        !!mytaStreams.lease &&
        !isDebugMode
      : isEventRun,
    streamingProvider,
    // The seat this visit streamed from, for reconciliation. GameCast's ARN
    // arrives in a websocket handler and needs the state above; the lease is a
    // render value, so it needs no hop through one.
    streamSessionId: isMytaStreams
      ? mytaStreams.lease?.leaseId ?? null
      : billingStreamSessionId,
    invoiceId: billingInvoiceId,
    isGamecastCrash,
    isGamecastTimeout,
    cutReason:
      isMytaStreams && mytaStreams.cutReason
        ? MYTA_CUT_BILLING_REASONS[mytaStreams.cutReason]
        : null,
    isWebsocketOpen,
    sendJSONMessageToWebSocket,
    getLeaveEventReason,
  });

  // Last resort, never the plan: the stream is up and playing but nobody ever told us which room the user is
  // in. That used to be invisible (the loader dropped onto a blank world); holding the loader until the
  // picture is real made it honest but also made it terminal — an entry that cannot resolve a room simply
  // never finished. The hub knows which room it asked UE to load, so after a grace period take that one and
  // let the user in. Long enough that the normal path (a few seconds) always wins.
  useEffect(() => {
    if (
      !mytaStreams.playStream ||
      currentParticipant?.roomId ||
      !rooms.length
    ) {
      return;
    }

    const timer = setTimeout(() => {
      mytaverseEventLogger.log(
        'Stream is playing but no room was ever announced — falling back to the room this entry asked for',
      );
      restoreSeatedRoom(teleportingToRoom?.map?.ueIdentifier);
    }, SEATED_ROOM_FALLBACK_MS);

    return () => clearTimeout(timer);
  }, [
    mytaStreams.playStream,
    currentParticipant?.roomId,
    rooms,
    teleportingToRoom,
    restoreSeatedRoom,
  ]);

  const { avatarMessage } = useAvatarMessage({
    currentSkin: { avatarId: user?.activeAvatarId || '' } as IAvatarSkin,
    currentEvent,
    customAvatarUrl: user?.customAvatarUrl || '',
  });

  const {
    connectedPod,
    coreweaveLoading,
    dataChannelOpened,
    handleInitializeButton,
    isPlayButtonPressed,
    playStream,
    rootElement,
    setRootElement,
    setWebSocketAddress,
    togglePlayStream,
    webSocketAddress,
  } = useCoreweave({
    currentEvent,
    currentParticipant,
    currentRoom,
    gameSound,
    initMessageHandler,
    initMessageSended,
    muted,
    pixelStreamingClient,
    coreweavePixelStreaming,
    setGameReadyToPlay,
    setInitMessageSended,
    setCoreweavePixelStreaming,
    ue5CoreWeaveDisabled,
    streamingProvider,
    avatarMessage,
  });

  const {
    streamId: gameCastStreamId,
    setStreamId: setGameCastStreamId,
    streamRegion: gameCastStreamRegion,
    setStreamRegion: setGameCastStreamRegion,
  } = useInitGameCastStream(
    currentEvent,
    streamingProvider,
    pixelStreamingClient,
  );

  const {
    isStreamLoaded,
    isStreamLoading,
    setIsStreamLoaded,
    setIsStreamLoading,
    videoRef,
    audioRef,
    abortGamecastConnection,
    processActiveStreamSession,
    gamecastClientRef,
    sendMytaverseActions,
    initGameliftStreamsSessionStatusTimeout,
    clearGameliftStreamsSessionStatusTimeout,
    setReconnectGamecast,
    resetReconnectGamecastAttempts,
  } = useGamecast({
    currentEventId,
    currentRoom,
    trackAnalytics: trackEventAnalytics,
    gameSound,
    currentParticipant,
    setGameCastStreamId,
    initMessageHandler,
    gameCastStreamRequestSent: gameCastStreamRequestSended,
    setGameCastStreamRequestSent: setGameCastStreamRequestSended,
    setInitMessageSent: setInitMessageSended,
    gameCastStreamId,
    gameCastStreamRegion,
    setGameCastStreamRegion,
    setStreamingProvider,
    setIsGamecastCrash,
    setCurrentRoom,
    setCurrentParticipant,
    isGamecastCrash,
    currentEventParticipant,
    websocketSessionId,
    currentEvent,
    gamecastStats,
    generateNewGameliftStreamsSessionKey,
    isGamecastTimeout,
    setIsGamecastTimeout,
    pixelStreamingClient,
    externalKeyboardConnected,
    setShowExternalKeyboardOverlay,
  });

  const handleUEInitializationTimeout = useCallback(() => {
    mytaverseEventLogger.log(
      `UE initialization stuck for ${REACT_APP_UE_INITIALIZATION_TIMEOUT_SECONDS} seconds`,
    );

    setReconnectGamecast(true);
  }, [setReconnectGamecast]);

  const clearUEInitializationTimeout = useCallback(() => {
    if (ueInitializationTimeoutRef.current) {
      clearTimeout(ueInitializationTimeoutRef.current);
      ueInitializationTimeoutRef.current = null;
    }
  }, []);

  const initUEInitializationTimeout = useCallback(() => {
    clearUEInitializationTimeout();

    mytaverseEventLogger.log(
      `Start UE initialization timer (${REACT_APP_UE_INITIALIZATION_TIMEOUT_SECONDS} seconds)`,
    );

    ueInitializationTimeoutRef.current = setTimeout(
      handleUEInitializationTimeout,
      Number(REACT_APP_UE_INITIALIZATION_TIMEOUT_SECONDS) * 1000,
    );
  }, [
    setIsGamecastTimeout,
    clearUEInitializationTimeout,
    handleUEInitializationTimeout,
  ]);

  const handleGamecastStreamSession = useCallback(
    async ({
      status,
      gamecastStreamSession,
      sessionKey,
    }: {
      status: StreamSessionStatus;
      gamecastStreamSession: IStartStreamSessionOutput | null;
      sessionKey?: string;
    }) => {
      const expectedSessionKey = gameliftStreamsSessionKeyRef.current;
      const isCurrentAttempt =
        !!sessionKey &&
        !!expectedSessionKey &&
        expectedSessionKey === sessionKey;

      mytaverseEventLogger.log(
        `Handle WS message: sessionId: ${
          gamecastStreamSession?.Arn
            ? gamecastStreamSession?.Arn.split('/').pop()
            : ''
        }, status: ${status}, sessionKey: ${sessionKey}`,
      );

      if (isCurrentAttempt) {
        clearGameliftStreamsSessionStatusTimeout();

        // Anchor line 2 (MV-1077): mirror the breadcrumb above as a Sentry
        // *event* (the analyser ingests events, not breadcrumbs), tagged with
        // the websocket session id so both session ids land on one event and
        // the analyser can stitch the session (0.90 'stitched' tier). The
        // emitter self-skips until streamSessionId/status/sessionKey are all
        // present, so it only fires once this attempt's stream session exists.
        reportStreamInitializationEvent({
          streamSessionId: gamecastStreamSession?.Arn
            ? gamecastStreamSession.Arn.split('/').pop()
            : undefined,
          status,
          sessionKey,
          websocketSessionId,
        });
      } else {
        mytaverseEventLogger.debug(
          `Skip side-effects for unknown sessionKey: expected ${expectedSessionKey}, got ${sessionKey}`,
        );
      }

      switch (status) {
        case StreamSessionStatus.ACTIVATING: {
          if (isCurrentAttempt) {
            initGameliftStreamsSessionStatusTimeout();
          }
          break;
        }

        case StreamSessionStatus.ACTIVE: {
          if (!isCurrentAttempt) {
            break;
          }

          if (!gamecastStreamSession) {
            navigateToEventsPage({
              reason: LeaveEventReason.ExperienceFailure,
              message: 'notifications.noAvailableStreamingProviders',
              isModal: false, // or true?
            });
            break;
          }

          const { isStarted, isAborted } = await processActiveStreamSession(
            gamecastStreamSession,
          );

          if (isStarted) {
            if (gamecastStreamSession.Arn) {
              setBillingStreamSessionId(gamecastStreamSession.Arn);
            }

            resetReconnectGamecastAttempts();
            trackEventAnalytics(AnalyticsType.FinishGameLiftStreamsInit);
            trackEventAnalytics(AnalyticsType.StartUeInitialization);
            setIsStreamLoading(false);
            setIsStreamLoaded(true);

            if (currentEventParticipant) {
              sendMytaverseActions(currentEventParticipant);
            }

            if (isSinglePlayerEvent(currentEvent)) {
              initUEInitializationTimeout();
            }

            mytaverseEventLogger.log(
              `Started gamelift streams session: ${gamecastStreamSession.Arn?.split(
                '/',
              ).pop()}`,
            );
          } else if (!isAborted) {
            navigateToEventsPage({
              reason: LeaveEventReason.ExperienceFailure,
              message: 'notifications.noAvailableStreamingProviders',
              isModal: false, // or true?
            });
          }

          break;
        }

        case StreamSessionStatus.ERROR: {
          if (!isCurrentAttempt) {
            break;
          }

          mytaverseEventLogger.log('Gamecast connection retry');
          setReconnectGamecast(true);

          break;
        }

        case StreamSessionStatus.STREAM_RETRY: {
          if (!isCurrentAttempt) {
            break;
          }

          // Transient backend failure — retry via reconnect (bounded by
          // MAX_GAMECAST_RECONNECT_ATTEMPTS). NO_MACHINES kicks immediately instead.
          mytaverseEventLogger.log(
            'Transient stream-start failure — reconnecting gamecast',
          );
          setReconnectGamecast(true);

          break;
        }

        case StreamSessionStatus.NO_MACHINES: {
          if (isCurrentAttempt) {
            navigateToEventsPage({
              reason: LeaveEventReason.ExperienceFailure,
              message: 'notifications.noAvailableStreamingProviders',
              isModal: false,
            });
          }
          break;
        }

        default:
          break;
      }
    },
    [
      processActiveStreamSession,
      setIsStreamLoading,
      setIsStreamLoaded,
      currentEventParticipant,
      sendMytaverseActions,
      navigateToEventsPage,
      pixelStreamingClient,
      initGameliftStreamsSessionStatusTimeout,
      clearGameliftStreamsSessionStatusTimeout,
      setReconnectGamecast,
      initUEInitializationTimeout,
      currentEvent,
    ],
  );

  const loadSessionIds = useCallback(() => {
    const websocketSessionIdsStr = sessionStorage.getItem('websocketSessionId');

    return websocketSessionIdsStr ? JSON.parse(websocketSessionIdsStr) : [];
  }, []);

  useEffect(() => {
    if (!websocketSessionId) {
      return;
    }

    const websocketSessionIds = loadSessionIds();
    websocketSessionIds.push(websocketSessionId);

    sessionStorage.setItem(
      'websocketSessionId',
      JSON.stringify(websocketSessionIds),
    );
  }, [websocketSessionId]);

  // MV-1816: the CMS AI toggles now arrive live over EVENT_SETTINGS_UPDATED, so
  // HomeBottomBar's assistant gate can flip mid-session and unmount the whole
  // AssistantProvider subtree — which does not stop its audio. Tear it down
  // explicitly on that transition, next to the leave-the-event-page teardown
  // below, which makes the same stopAssistantAudio() call.
  useAssistantTeardownOnDisable(currentEventId, currentEvent?.settings);

  useEffect(() => {
    mytaverseEventLogger.log(`Open event ${currentEventId} page`);
    resetLeaveEventReason();
    return () => {
      mytaverseEventLogger.log(`Leave event ${currentEventId} page`);
      clearUEInitializationTimeout();
      clearGameliftStreamsSessionStatusTimeout();
      abortGamecastConnection();
      stopAssistantAudio();
      if (!isLogoutClicked()) {
        sendJSONMessageToWebSocket({
          action: 'LEAVE_EVENT',
          reason: getLeaveEventReason(),
        });
      }
    };
  }, []);

  const cleanStates = useCallback(() => {
    setRooms([]);
    setParticipants([]);
    setOpenChat(false);
    setCurrentParticipant(undefined);
    setCurrentRoom(null);
    setCurrentEvent(undefined);
    setTeleportEnabled(false);
  }, [
    setRooms,
    setParticipants,
    setCurrentParticipant,
    setCurrentRoom,
    setCurrentEvent,
    sendJSONMessageToWebSocket,
  ]);

  useBackButton({
    cleanStates,
    initMessageSended,
    ue5WebsocketConnected,
    setUe5WebsocketConnected,
    currentEventId: currentEventId ?? null,
  });

  useEffect(() => {
    if (currentRoom) {
      localStorage.setItem('currentRoomId', currentRoom.roomId);
      mytaverseEventLogger.log(`Room changed to ${currentRoom.name}`);

      mytaverseEventLogger.log('Clear UE initialization timeout');
      clearUEInitializationTimeout();
    }
  }, [currentRoom, clearUEInitializationTimeout]);

  useEffect(() => {
    if (userFiles) {
      localStorage.setItem('userFiles', JSON.stringify(userFiles) as string);
    }
  }, [userFiles, currentRoom]);

  useEffect(() => {
    if (currentRoom) {
      setUserFiles([]);
    }
  }, [currentRoom]);

  // Only a move between two real rooms clears the map-editor state below: `currentRoom` is derived
  // and useParticipantsState blanks it on any pass where the participant's own roomId is empty (see
  // the reload-rebind note above), so reacting to that null would throw away a live report.
  const mapEditorRoomScopeRef = useRef<string | null>(null);

  useEffect(() => {
    const { scope, shouldClear } = resolveMapEditorRoomScope(
      mapEditorRoomScopeRef.current,
      currentRoom?.roomId ?? null,
    );
    mapEditorRoomScopeRef.current = scope;

    if (!shouldClear) {
      return;
    }

    setCurrentGeometryRooms(null);
    setCurrentGeometryRefusals(null);
    // Cleared with the rooms it belongs to. The two are halves of one report, so a lobby left
    // behind here would describe the room the author just walked out of — harmless only for as
    // long as every reader happens to consult the rooms first.
    setCurrentGeometryStartRoom(null);
    setPreviewRefusals(null);
    setEditorModeState(null);
  }, [currentRoom]);

  useEffect(() => {
    const effectiveBrandingId =
      currentBrandingId ?? currentRoom?.activeBrandingId ?? null;
    if (!effectiveBrandingId) {
      setBrandingSpots([]);
      return;
    }

    BrandingsService.getBrandingSpots(effectiveBrandingId, { limit: 200 })
      .then((res) => setBrandingSpots(res.spots ?? []))
      .catch(() => setBrandingSpots([]));
  }, [currentBrandingId, currentRoom, isBrandingModeEnabled]);

  useEffect(() => {
    const effectiveBrandingId =
      currentBrandingId ?? currentRoom?.activeBrandingId ?? null;
    if (!effectiveBrandingId) {
      setBrandingHotspots([]);
      return;
    }

    BrandingsService.getBrandingHotspots(effectiveBrandingId, { limit: 200 })
      .then((res) => setBrandingHotspots(res.hotspots ?? []))
      .catch(() => setBrandingHotspots([]));
  }, [currentBrandingId, currentRoom, isBrandingModeEnabled]);

  // The branding record itself, for the presentation values the hub reads off
  // it (assistant icon and name). Keyed the same way as the spots/hotspots
  // fetches above, so the editor previews the branding it has open while
  // everyone else sees the one active in the room.
  useEffect(() => {
    const effectiveBrandingId =
      currentBrandingId ?? currentRoom?.activeBrandingId ?? null;
    if (!effectiveBrandingId) {
      setCurrentBranding(null);
      return;
    }

    let cancelled = false;

    BrandingsService.getBranding(effectiveBrandingId)
      .then((branding) => {
        if (!cancelled) {
          setCurrentBranding(branding ?? null);
        }
      })
      .catch((error) => {
        console.error(
          `Failed to load branding for the assistant identity: ${
            (error as Error)?.message
          }`,
          { brandingId: effectiveBrandingId },
        );
        if (!cancelled) {
          setCurrentBranding(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentBrandingId, currentRoom]);

  useAsyncEffect(async () => {
    if (!currentRoom) {
      return;
    }

    if (pointsOfInterest) {
      setPointsOfInterest(null);
    }

    let pointsInterest: IPointOfInterest[] = [];

    try {
      pointsInterest = await EventsService.getPointOfInterest(
        currentRoom.roomId,
      );
    } catch (e) {
      pointsInterest = [];
    }

    if (pointsInterest?.length) {
      setPointsOfInterest(pointsInterest);
    }
  }, [currentRoom]);

  useEffect(() => {
    if (!currentEventId) {
      return;
    }

    sendJSONMessageToWebSocket({
      action: muted
        ? SendMutedStateMessageEnum.MUTE
        : SendMutedStateMessageEnum.UNMUTE,
      timestamp: Date.now(),
    });
  }, [muted, currentEventId, sendJSONMessageToWebSocket]);

  const canSpeak = useMemo(() => {
    return getClientSpecificCanSpeak(currentRoom);
  }, [currentRoom]);

  const canShowCamera = useMemo(() => {
    return getClientSpecificCanShowWebCameraOnAvatar(currentRoom);
  }, [currentRoom]);

  const canShareCamera = useMemo(() => {
    return getClientSpecificCanShareWebCameraOnScreen(
      currentRoom,
      currentEvent,
      userPermissions,
    );
  }, [currentEvent, currentRoom, userPermissions]);

  const canShareScreen = useMemo(() => {
    return getClientSpecificCanShareScreenOnScreen(
      currentRoom,
      currentEvent,
      userPermissions,
    );
  }, [currentEvent, currentRoom, userPermissions]);

  useAsyncEffect(async () => {
    if (loadingNewParticipants) {
      return;
    }
    const loadingParticipants = participants.filter(
      (p) => p.state === ParticipantState.Loading,
    );
    if (loadingParticipants.length === 0 || !currentEventId) {
      return;
    }
    setLoadingNewParticipants(true);
    const eventParticipant = await EventsService.getEvent(currentEventId);
    if (eventParticipant.event?.participants?.length !== 0) {
      setParticipants((prev) => {
        return prev.map((participant) => {
          if (participant.state === ParticipantState.Loaded) {
            return participant;
          }
          const loadedParticipant = eventParticipant.event?.participants.find(
            (p) => p.participantId === participant.participantId,
          );
          if (!loadedParticipant) {
            return participant;
          }
          const { name } = loadedParticipant;
          return {
            ...participant,
            state: ParticipantState.Loaded,
            name: name,
            fullName: name,
          };
        });
      });
    }
    setLoadingNewParticipants(false);
  }, [loadingNewParticipants, participants, currentEventId]);

  useEffect(() => {
    if (currentRoom && currentRoom.dolbySpatialAudioScale) {
      setCurrentRoomDolbySpatialAudioScale(currentRoom.dolbySpatialAudioScale);
    }
  }, [currentRoom]);

  // TODO merge it with load participant settings data
  useAsyncEffect(async () => {
    try {
      if (currentEventId && userId) {
        setLoading(true);
        setEventLoaded(false);

        setPreviousCustomAvatar(null);
        setPreviousSkin(null);

        let eventParticipant: IEventParticipant;

        const participantEventsPromise =
          ParticipantsService.getCurrentParticipantEvents();
        participantEventsPromise
          .then((events) =>
            setEvents(
              events.events.map(
                (participantEvent) => participantEvent.event as IEvent,
              ),
            ),
          )
          .catch((e) =>
            mytaverseEventLogger.log(
              `Failed to load participant events list: ${(e as Error).message}`,
            ),
          );

        try {
          eventParticipant = await EventsService.getEvent(currentEventId, {
            params: { sessionId: websocketSessionId },
          });
        } catch (e: any) {
          return navigateToEventsPage({
            reason: LeaveEventReason.ExperienceFailure,
            message: 'notifications.loadEventDataError',
            type: NOTIFICATION_TYPES.ERROR,
          });
        }

        if (eventParticipant.event?.status !== EventStatus.ACTIVE) {
          mytaverseEventLogger.log(
            'Event status inactive, redirect to /events',
            { eventId: currentEventId, status: eventParticipant.event?.status },
          );
          return navigateToEventsPage({
            reason: LeaveEventReason.ExperienceCanceled,
            message: 'notifications.experienceCanceled',
            type: NOTIFICATION_TYPES.WARNING,
          });
        }

        setCurrentEvent(eventParticipant.event);

        const { eventRelease } = eventParticipant.event;
        mytaverseEventLogger.log(
          `UE server build: ${formatBuildVersionLabel(
            eventRelease?.serverVersion,
            eventRelease?.serverChangeset,
          )}`,
        );
        mytaverseEventLogger.log(
          `EPixel streaming client build (release): ${formatBuildVersionLabel(
            eventRelease?.clientVersion,
            eventRelease?.clientChangeset,
          )}`,
        );

        if (
          eventParticipant.event &&
          eventParticipant.event.loadingScreenAsset &&
          typeof eventParticipant.event.loadingScreenAsset !== 'string' &&
          'url' in eventParticipant.event.loadingScreenAsset
        ) {
          preloadVideos([
            (eventParticipant.event.loadingScreenAsset as any).url as string,
          ]);
        }

        setTeleportEnabled(
          eventParticipant.event?.eventTags
            ? eventParticipant.event.eventTags
                .map((tag) => tag.toLowerCase())
                .every(
                  (tag) => !['officehours', 'baseofficehours'].includes(tag),
                )
            : true,
        );

        const rooms = eventParticipant.event?.rooms || [];

        setRooms(rooms);
        const defaultRoom = rooms.find((room) => room.isDefault);
        setTeleportingToRoom(defaultRoom || null);

        const participants = eventParticipant.event?.participants
          ? eventParticipant.event.participants.map((participant) => ({
              ...participant,
              fullName: participant.name,
              state: ParticipantState.Loaded,
            }))
          : [];

        setParticipants(participants);

        const currentParticipant = participants.find(
          (participant) => participant.participantId === userId,
        );

        if (currentParticipant) {
          setCurrentParticipant({ ...currentParticipant });
        }

        const { event, ...currentEventParticipant } = eventParticipant;
        setCurrentEventParticipant(currentEventParticipant);

        setDolbyToken(eventParticipant.dolbyToken as string);
        setStreamChatToken(eventParticipant.streamChatToken as string);
        setStreamChatMemberId(eventParticipant.streamChatMemberId as string);
        setUnavailableServices(eventParticipant.unavailableServices ?? []);

        if (user?.roleScopes) {
          setUserPermissions((prevState: ParticipantScopes[]) => {
            if (prevState.length > 0) {
              return prevState;
            }

            const resolved = getClientSpecificUserEventPermissions({
              role: eventParticipant.role,
              roleScopes: user.roleScopes,
            });

            console.log(
              '[userPermissions] userPermissions resolved:',
              resolved,
              'role:',
              eventParticipant.role,
              'roleScopes:',
              user.roleScopes,
            );

            return resolved;
          });

          const newUserRole = getClientSpecificUserEventRole();

          if (newUserRole !== null) {
            setCurrentEventParticipant(
              (prevState: IEventParticipant | null) => {
                if (prevState) {
                  return {
                    ...prevState,
                    role: newUserRole as string,
                  };
                }

                return prevState;
              },
            );

            setCurrentParticipant((prevState) => {
              if (prevState) {
                return {
                  ...prevState,
                  role: newUserRole,
                };
              }

              return prevState;
            });
          }
        }

        const followingData = await EventsService.getFollowingData(
          currentEventId,
        );

        const { pending, accepted } = mapFollowers(followingData.followers);

        setPendingFollowersData(pending);
        setAcceptedFollowersData(accepted);

        setEventLoaded(true);
        setLoading(false);
      } else {
        setCurrentEvent(undefined);
        setTeleportEnabled(false);
        setEventLoaded(false);

        setRooms([]);
        setCurrentRoom(null);

        setParticipants([]);
        setCurrentParticipant(undefined);
      }
    } catch (error) {
      setEventLoaded(false);
      setLoading(false);
      mytaverseEventLogger.log((error as Error).message);
      navigateToEventsPage({
        reason: LeaveEventReason.ExperienceFailure,
        title: 'notifications.badRequest',
        message: (error as Error).message,
      });
    }
  }, [userId, currentEventId, user?.roleScopes, websocketSessionId]);

  const ownerTeleportToRoomPreserveData = React.useCallback(() => {
    if (acceptedFollowersData.length === 0 || !currentParticipant) return;

    const followers = acceptedFollowersData
      .filter(
        (follower) => follower.adminId === currentParticipant.participantId,
      )
      .map((follower) => follower.userId);

    if (followers.length === 0) return;

    setPreserveAcceptedFollowersId([...new Set(followers)]);
  }, [currentParticipant, acceptedFollowersData]);

  const ownerTeleportToRoom = React.useCallback(() => {
    if (
      preserveAcceptedFollowersId.length === 0 ||
      !currentParticipant ||
      !currentEventId
    )
      return;

    sendJSONMessageToWebSocket({
      action: WebsocketAction.OwnerTeleport,
      ownerId: currentParticipant.participantId,
      followerIds: [...preserveAcceptedFollowersId],
      ownerRoomId: currentParticipant.roomId,
      gameSessionId: currentParticipant.gameSessionId || null,
    });
  }, [
    currentParticipant,
    acceptedFollowersData,
    preserveAcceptedFollowersId,
    sendMessageToEvent,
    currentEventId,
  ]);

  useEffect(() => {
    if (isParticipantRoomChanged) {
      ownerTeleportToRoom();
      setIsParticipantRoomChanged(false);
      setPreserveAcceptedFollowersId([]);
    }
  }, [
    isParticipantRoomChanged,
    ownerTeleportToRoom,
    setPreserveAcceptedFollowersId,
  ]);

  // JOIN_TO_EVENT is what registers THIS websocket connection with the event, so it has to be sent once per
  // session — not once per participant. Gating it on `participant.eventId !== event.eventId` skipped it for
  // every re-entry, because that field is server state that outlives the socket: after a reload the record
  // still says "in this event" while the connection that earned it is gone. Measured 2026-08-12 — a reloaded
  // tab left GET /events/{id}/connections returning ZERO connections, so nothing could be routed to it: the
  // hub never learned which room its own user was in (blank world), and the slot's UE re-sent JOINED_TO_ROOM
  // twice a second into the void while its own 10 s no-incoming-message watchdog cycled its socket.
  const joinedEventRef = useRef<string | null>(null);

  useEffect(() => {
    if (!currentEvent || !currentParticipant || !websocketSessionId) {
      return;
    }

    const joinKey = `${websocketSessionId}:${currentEvent.eventId}`;

    if (joinedEventRef.current === joinKey) {
      return;
    }

    joinedEventRef.current = joinKey;

    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref') || 'hub';

    mytaverseEventLogger.log(
      `Announcing JOIN_TO_EVENT for session ${websocketSessionId}`,
    );

    sendJSONMessageToWebSocket({
      action: 'JOIN_TO_EVENT',
      timestamp: Date.now(),
      eventId: currentEvent.eventId,
      ref,
      visitId: visitIdRef.current,
    });
  }, [currentEvent, currentParticipant, websocketSessionId]);

  const updateParticipant: UpdateParticipantType = useCallback(
    (participantId, newParticipantInfo) => {
      if (
        currentParticipant &&
        currentParticipant.participantId === participantId
      ) {
        setCurrentParticipant(
          (prevParticipant) =>
            ({
              ...prevParticipant,
              ...newParticipantInfo,
              isSpeaker: !!prevParticipant?.isSpeaker,
            } as IParticipant),
        );
      }

      setParticipants((prevParticipants) =>
        prevParticipants.map((prevParticipant) =>
          prevParticipant.participantId === participantId
            ? {
                ...prevParticipant,
                ...newParticipantInfo,
              }
            : prevParticipant,
        ),
      );
    },
    [currentParticipant],
  );

  // Re-resolve the current user's event permissions and role when the role
  // changes mid-session (MV-1303). Mirrors the initial-load resolution at
  // event load, but unconditionally — the first-load path only resolves once.
  const recomputeUserEventPermissions = useCallback(
    (role: string) => {
      if (!user?.roleScopes) {
        return;
      }

      const resolved = getClientSpecificUserEventPermissions({
        role,
        roleScopes: user.roleScopes,
      });

      setUserPermissions(resolved);

      const clientSpecificRole = getClientSpecificUserEventRole();
      const effectiveRole =
        clientSpecificRole !== null ? clientSpecificRole : role;

      setCurrentEventParticipant((prevState) =>
        prevState ? { ...prevState, role: effectiveRole as string } : prevState,
      );
    },
    [user, setUserPermissions],
  );

  // When the current user is in drone mode but loses hub-admin-tools access
  // (e.g. their experience role is downgraded in realtime, MV-1303), UE5 must
  // be told to leave the drone camera. Otherwise the detached drone camera
  // stays active with no avatar and the user appears stuck in first-person.
  // This covers the permission-loss path that unmounts <AdminTools/> — its
  // local toggle would drop the drone state silently, never sending disable.
  useEffect(() => {
    if (!isDroneMode) {
      return;
    }

    const hasAdminTools = !!userPermissions?.includes(
      ParticipantScopes.HubAdminTools,
    );

    if (hasAdminTools) {
      return;
    }

    sendMessageToUnreal({ action: WebsocketAction.DisableDroneCamera });
    setIsDroneMode(false);
  }, [userPermissions, isDroneMode, sendMessageToUnreal]);

  const selectNavigationMode = useCallback(
    async (mode: NavigationModeType) => {
      try {
        await ParticipantsService.updateCurrentParticipantProfile({
          navigationMode: mode,
        });
        setCurrentEventParticipant((prev) => ({
          ...prev,
          navigationMode: mode,
        }));
      } catch (error) {
        console.error(
          `Failed to update navigation mode: ${(error as Error).message}`,
          { participantId: currentParticipant?.participantId },
        );
      }
    },
    [currentParticipant?.participantId],
  );

  const resetPreviousSkin = useCallback(() => {
    if (previousSkin) {
      setPreviousSkin(null);
    }
  }, [previousSkin, setPreviousSkin]);

  const resetPreviousCustomSkin = useCallback(() => {
    if (previousCustomAvatar) {
      setPreviousCustomAvatar(null);
    }
  }, [previousCustomAvatar, setPreviousCustomAvatar]);

  const setRoomDolbySpatialAudioScale = useCallback(
    (roomId: string, dolbySpatialAudioScale: number) => {
      if (currentRoom && currentRoom.roomId === roomId) {
        setCurrentRoomDolbySpatialAudioScale(dolbySpatialAudioScale);
      }
      /*
      setCurrentRoom((prev) => {
        if (!prev || prev.id !== roomId) {
          return prev;
        }

        return {
          ...prev,
          dolbySpatialAudioScale,
        };
      });
       */

      setRooms((prev) =>
        prev.map((room) => {
          if (room.roomId === roomId) {
            return {
              ...room,
              dolbySpatialAudioScale,
            };
          }

          return room;
        }),
      );
    },
    [currentRoom],
  );

  /**
   * Applies a CMS edit of the room's assistant fallback identity.
   *
   * Writes `rooms` and deliberately not `currentRoom`. A new `currentRoom`
   * object identity is what the hub treats as "the participant changed room":
   * five effects hang off it, and one of them empties `userFiles` — which the
   * next effect then persists over the stored copy. An assistant icon changing
   * in the CMS must not cost everyone in the room their files toolbox.
   *
   * `rooms` is the record the identity is read from (see `useAssistantIdentity`),
   * and nothing keys off its object identity, so this stays inert for every
   * consumer that does not ask for these two fields.
   *
   * @param roomId - Room the edit belongs to.
   * @param patch - Fields the broadcast carried; a `null` clears one.
   */
  const setRoomAssistantIdentity = useCallback(
    (roomId: string, patch: Pick<IRoom, 'assistantIcon' | 'assistantName'>) => {
      setRooms((prev) =>
        prev.map((room) =>
          room.roomId === roomId ? { ...room, ...patch } : room,
        ),
      );
    },
    [],
  );

  const updateRoomScale = useCallback(
    async (value: number) => {
      if (currentRoom && currentEventId) {
        await EventsService.updateRoomSpatialScale(
          currentRoom.roomId as string,
          value,
          currentEventId,
        );

        setRoomDolbySpatialAudioScale(currentRoom.roomId, value);
      }
    },
    [currentRoom, currentEventId, setRoomDolbySpatialAudioScale],
  );

  const teleportToRoom = React.useCallback(
    (room: IRoom | null) => {
      setTeleportingToRoom(room);
      //TODO add is speaker to message
      if (room && currentParticipant && !isTeleporting) {
        MytaverseLogger.log(`Teleporting to room: ${room.roomId} ${room.name}`);
        setIsTeleporting(true);
        ownerTeleportToRoomPreserveData();
        sendJSONMessageToWebSocket({
          action: WebsocketAction.TeleportToRoom,
          roomId: room.roomId,
          isSpeaker: currentParticipant.isSpeaker,
        });
      }
    },
    [
      setTeleportingToRoom,
      sendJSONMessageToWebSocket,
      currentParticipant,
      setIsTeleporting,
      isTeleporting,
      ownerTeleportToRoomPreserveData,
    ],
  );

  const teleportToParticipant = React.useCallback(
    async (participant: IParticipant | null) => {
      if (!participant || !currentRoom) {
        return;
      }

      MytaverseLogger.log(
        `Teleporting to participant: ${participant.participantId} ${participant.fullName}`,
      );

      const { roomId } = await ParticipantsService.getParticipantRoom(
        participant.participantId,
      );

      if (currentRoom.roomId !== roomId) {
        setIsTeleporting(true);
        ownerTeleportToRoomPreserveData();
      }

      sendMessageToUnreal({
        action: WebsocketAction.TeleportToParticipant,
        participantId: participant.participantId,
        roomId: currentRoom?.roomId,
        CurrentGameSessionId: participant.gameSessionId || null,
      });
    },
    [currentRoom, sendMessageToUnreal, ownerTeleportToRoomPreserveData],
  );

  const teleportRef = useRef<NodeJS.Timeout | null>(null);
  const ue5WebsocketConnectedRef = useRef(false);

  useEffect(() => {
    ue5WebsocketConnectedRef.current = ue5WebsocketConnected;
  }, [ue5WebsocketConnected]);

  useEffect(() => {
    if (isTeleporting && currentRoom && !teleportRef.current) {
      teleportRef.current = setInterval(() => {
        if (ue5WebsocketConnectedRef.current && teleportRef.current) {
          setIsTeleporting(false);
          clearInterval(teleportRef.current);
        }
      }, 5000);

      return;
    }

    if (teleportRef.current && !isTeleporting) {
      clearInterval(teleportRef.current);
      teleportRef.current = null;
      return;
    }

    return () => {
      if (teleportRef.current) {
        clearInterval(teleportRef.current);
        teleportRef.current = null;
      }
    };
  }, [isTeleporting, currentRoom]);

  useEffect(() => {
    if (isTeleportingToRoomByUnreal) {
      ownerTeleportToRoomPreserveData();
    }
  }, [isTeleportingToRoomByUnreal, ownerTeleportToRoomPreserveData]);

  useEffect(() => {
    if (
      currentEvent?.eventTags?.includes('ExtraLaunchArgsRequired') &&
      (!launchArgs || launchArgs.length === 0)
    ) {
      mytaverseEventLogger.log(
        'Redirect to events page because no extra launch args',
      );
      navigateToEventsPage({
        reason: LeaveEventReason.ExperienceFailure,
        message: 'notifications.noExtraLaunchArgs',
      });
    }
  }, [currentEvent?.eventTags, launchArgs, navigateToEventsPage]);

  const resetKeyboardPressedKeys = React.useCallback(() => {
    const videoElement = document.getElementById('gamecastVideoTag');

    if (videoElement) {
      const keysPressed = [
        'W',
        'A',
        'S',
        'D',
        'ArrowUp',
        'ArrowLeft',
        'ArrowDown',
        'ArrowRight',
      ].map((key) => {
        return {
          key,
          code: key.length === 1 ? `Key${key}` : key,
          bubbles: true,
          cancelable: true,
        };
      });

      Object.values(keysPressed).forEach((event) => {
        MytaverseLogger.log(`Dispatch keyup: ${event.key}`);
        videoElement.dispatchEvent(new KeyboardEvent('keyup', event));
      });
    }
  }, []);

  const setMutedHandler = React.useCallback(
    (value: boolean | undefined) => {
      setMuted(value || !muted);
      trackEventAnalytics(
        value || !muted ? AnalyticsType.MicOff : AnalyticsType.MicOn,
      );
    },
    [muted, setMuted, trackEventAnalytics],
  );

  const toggleRaiseHand = React.useCallback(() => {
    const nextState = !isHandRaised;

    setIsHandRaised(nextState);

    sendJSONMessageToWebSocket({
      action: 'RAISE_HAND',
      active: nextState ? true : false,
    });

    trackEventAnalytics(AnalyticsType.NavbarButtonClick, {
      name: 'raise-hand',
      active: nextState,
    });
  }, [isHandRaised, sendJSONMessageToWebSocket, trackEventAnalytics]);

  // A live EVENT_SETTINGS_UPDATED can switch RAISE_HAND off while a hand is up.
  // The button and the reaction unmount with the flag, so nothing would ever
  // lower that hand again: the avatar keeps it raised, SYNC_DATA_WEB keeps
  // reporting it, and the participant stays in the trainer's raised-hands list
  // for the rest of the session. Lower it through the same toggle so UE and the
  // backend connection record hear about it too.
  React.useEffect(() => {
    if (
      isHandRaised &&
      !isEventFeatureEnabled(currentEvent, ExperienceFeatures.RaiseHand)
    ) {
      toggleRaiseHand();
    }
  }, [currentEvent, isHandRaised, toggleRaiseHand]);

  const setEmojiHandler = React.useCallback(
    (emoji: string | null) => {
      setEmoji(emoji);

      sendJSONMessageToWebSocket({
        action: 'SEND_EMOJI',
        emoji,
      });
    },
    [setEmoji, sendJSONMessageToWebSocket],
  );

  const setReactionHandler = React.useCallback(
    (reaction: string | null) => {
      setReaction(reaction);

      sendJSONMessageToWebSocket({
        action: 'TOGGLE_REACTION',
        reactionId: reaction,
      });
      trackEventAnalytics(AnalyticsType.ToggleReaction, { reaction });
    },
    [setReaction, sendJSONMessageToWebSocket, trackEventAnalytics],
  );

  const setSharingWindowFullscreenHandle = useCallback(() => {
    setSharingWindowFullscreen(!sharingWindowFullsceen);
  }, [setSharingWindowFullscreen, sharingWindowFullsceen]);

  if (loading) {
    return <EventFullLoadingPage />;
  }

  return (
    <Outlet
      context={{
        currentEventId: currentEventId as string,
        currentParticipantId: userId as string,

        currentEventParticipant,
        playVideoUrl,
        setPlayVideoUrl,
        reaction,
        emoji,
        muted,
        eventLoaded,
        userFiles,
        previousSkin,
        previousCustomAvatar,
        rooms,
        currentEvent,
        setCurrentEvent,
        currentRoom,
        currentRegion,
        currentParticipant,
        gameCastStreamRequestSended,
        isTeleporting,
        setIsTeleporting,
        teleportFinished,
        setTeleportFinished,
        setGameCastStreamRequestSended,
        setCurrentParticipant,
        setCurrentEventParticipant,
        initMessageHandler,
        gameReadyToPlay,
        setGameReadyToPlay,
        participants,
        teleportingToRoom,
        teleportEnabled,
        pointsOfInterest,
        loading,
        dolbyToken,
        streamChatToken,
        unavailableServices,
        streamChatMemberId,
        openLeaveRegionDialog,
        showControls,
        poiPreviewSrc,
        initMessageSended,
        sharingWindowFullsceen,
        participantsSound,
        selectedTeleportRoom,
        gameSound,
        openTeleportToRoomDialog,
        openAdminSettingsModal,
        openCameraPublisherDialog,
        setOpenCameraPublisherDialog,
        isStreamLoaded,
        isStreamLoading,
        setIsStreamLoaded,
        setIsStreamLoading,
        //streamSession,
        videoRef,
        audioRef,
        setOpenAdminSettingsModal,
        streamingProvider,
        gameCastStreamId,
        developersTerminalMessages,
        setDevelopersTerminalMessages,
        setGameCastStreamId,
        gameCastStreamRegion,
        setGameCastStreamRegion,
        setStreamingProvider,
        isNativeProviderCheckPending,
        billingInvoiceId,
        setBillingInvoiceId,
        handleBillingStale,
        mytaStreams,
        setGameSound: setGameSoundHandle,
        setUe5WebsocketConnected,
        ue5WebsocketConnected,
        isMinimizedScreenSharing,
        openDevelopersTerminal,
        setOpenDevelopersTerminal,
        setIsMinimizedScreenSharing,
        setOpenTeleportToRoomDialog,
        canSpeak,
        canShowCamera,
        canShareCamera,
        canShareScreen,
        shouldConnectToConference: !isSinglePlayerEvent(currentEvent),
        setSelectedTeleportRoom,
        setParticipantsSound: setParticipantsSoundHandle,
        assistantSound,
        setAssistantSound: setAssistantSoundHandle,
        setOpenLeaveRegionDialog,
        setSharingWindowFullscreen: setSharingWindowFullscreenHandle,
        setInitMessageSended,
        setPoiPreviewSrc,
        setShowControls,
        setParticipants,
        setReaction: setReactionHandler,
        setEventLoaded,
        setMuted: setMutedHandler,
        setEmoji: setEmojiHandler,
        isHandRaised,
        setIsHandRaised,
        toggleRaiseHand,
        setUserFiles,
        resetPreviousSkin,
        resetPreviousCustomSkin,
        setCurrentRoom,
        setCurrentRegion,
        currentRoomDolbySpatialAudioScale,
        cleanStates,
        connectedPod,
        coreweaveLoading,
        dataChannelOpened,
        handleInitializeButton,
        isPlayButtonPressed,
        pixelStreaming: coreweavePixelStreaming,
        mytaPixelStreaming,
        setMytaPixelStreaming,
        playStream,
        rootElement,
        setRootElement,
        setWebSocketAddress,
        togglePlayStream,
        webSocketAddress,
        setTeleportingToRoom,
        teleportToRoom,
        teleportToParticipant,
        isTeleportingToRoomByUnreal,
        setIsTeleportingToRoomByUnreal,
        setParticipantState,
        updateParticipant,
        recomputeUserEventPermissions,
        isDroneMode,
        setIsDroneMode,
        selectNavigationMode,

        setRoomDolbySpatialAudioScale,
        setRoomAssistantIdentity,
        updateRoomScale,
        showAvaturnModal,
        setShowAvaturnModal,
        // recreateAvaturnAvatar,
        // setRecreateAvaturnAvatar,
        trackEventAnalytics,
        pixelStreamingClient,
        pendingFollowersData,
        setPendingFollowersData,
        acceptedFollowersData,
        setAcceptedFollowersData,
        enableSoulmachinesAssistant,
        setEnableSoulmachinesAssistant,
        assistantScriptUpdateTick,
        setAssistantScriptUpdateTick,
        ue5CoreWeaveDisabled,
        setUe5CoreWeaveDisabled,
        isGamecastCrash,
        setIsGamecastCrash,
        events,
        gamecastStats,
        showGamecastStats,
        setShowGamecastStats,
        showExitButton,
        setShowExitButton,
        isHideWebUI,
        setIsHideWebUI,
        isHideWebUIExceptAssistant,
        setIsHideWebUIExceptAssistant,
        isHideAllExceptTopBar,
        setIsHideAllExceptTopBar,
        screenshotDownloadUrl,
        setScreenshotDownloadUrl,
        fallbackScreenshotDownloadUrlRef,
        isUEModalActionInProgress,
        setIsUEModalActionInProgress,
        processActiveStreamSession,
        gamecastClientRef,
        sendMytaverseActions,
        handleGamecastStreamSession,
        gameliftStreamsSessionKey: gameliftStreamsSessionKeyRef.current,
        generateNewGameliftStreamsSessionKey,
        showExternalKeyboardOverlay,
        setShowExternalKeyboardOverlay,
        externalKeyboardConnected,
        setExternalKeyboardConnected,
        isUserEnteringEvent,
        setIsUserEnteringEvent,
        showCustomSpotsEditor,
        setShowCustomSpotsEditor,
        showCustomizeSpotModal,
        setShowCustomizeSpotModal,
        customisationItemSelectedTick,
        setCustomisationItemSelectedTick,
        selectedCustomizationTag,
        setSelectedCustomizationTag,
        roomSpots,
        setRoomSpots,
        selectedCustomizationUeId,
        setSelectedCustomizationUeId,
        selectedCustomizationTargetSize,
        setSelectedCustomizationTargetSize,
        selectedCustomizationSpotType,
        setSelectedCustomizationSpotType,
        customizationSelectionRevision,
        setCustomizationSelectionRevision,
        isBrandingModeEnabled,
        setIsBrandingModeEnabled,
        brandingActionRequest,
        setBrandingActionRequest,
        brandingEditorFocus,
        setBrandingEditorFocus,
        isEditorInspectorOpen,
        setIsEditorInspectorOpen,
        assistantOpenRequestNonce,
        requestAssistantOpen,
        pendingAssistantRegionTrigger,
        requestAssistantRegionChange,
        suppressedCustomizationSelectIdRef,
        suppressNextCustomizationSelect,
        currentBrandingId,
        setCurrentBrandingId,
        brandingSpots,
        setBrandingSpots,
        brandingHotspots,
        setBrandingHotspots,
        currentBranding,
        setCurrentBranding,
        pendingEditorHotspotSelection,
        setPendingEditorHotspotSelection,
        requestEditorHotspotSelection,
        currentGeometryRooms,
        setCurrentGeometryRooms,
        currentGeometryRefusals,
        setCurrentGeometryRefusals,
        currentGeometryStartRoom,
        setCurrentGeometryStartRoom,
        liveBrandingEntities,
        setLiveBrandingEntities,
        previewRefusals,
        setPreviewRefusals,
        editorModeState,
        setEditorModeState,
        pendingHotspotPlacement,
        setPendingHotspotPlacement,
        hotspotLiveTransform,
        publishHotspotLiveTransform,
        realtimeEntities,
        upsertRealtimeEntity,
        removeRealtimeEntity,
        clearRealtimeEntities,
        reloadRealtimeEntities,
        bumpRealtimeEntitiesRevision,
        realtimeEntitiesRevision,
        realtimeEntityLiveTransform,
        publishRealtimeEntityLiveTransform,
        familiarizeState,
        setFamiliariseState,
        resetKeyboardPressedKeys,
        aiTriggerName,
        setAiTriggerName,
        dynamicAiTriggers,
        setDynamicAiTriggers,
        objectPoiId,
        setObjectPoiId,
        spotFullscreenViewId,
        setSpotFullscreenViewId,
        spotFullscreenViewTrigger,
        setSpotFullscreenViewTrigger,
        spotFullscreenViewStartAt,
        setSpotFullscreenViewStartAt,
      }}
    />
  );
};
